import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const POINTS = {
  task_complete: 10,
  skill_complete: 20,
  skill_progress: 5,
  app_advance: 15,
  diary_entry: 5,
} as const;

export type PointAction = keyof typeof POINTS;

const ACTION_LABELS: Record<PointAction, string> = {
  task_complete: '✅ Task completed!',
  skill_complete: '🎓 Skill mastered!',
  skill_progress: '📈 Skill progress updated!',
  app_advance: '💼 Application advanced!',
  diary_entry: '📔 Diary entry saved!',
};

export async function awardPoints(userId: string, action: PointAction, description?: string): Promise<void> {
  try {
    const { data: current } = await (supabase as any)
      .from('user_gamification')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    const c = current as any;
    const points = POINTS[action];
    const today = new Date().toISOString().split('T')[0];

    const updates: Record<string, any> = {
      user_id: userId,
      total_points: (c?.total_points || 0) + points,
      diary_streak: c?.diary_streak || 0,
      longest_diary_streak: c?.longest_diary_streak || 0,
      last_diary_date: c?.last_diary_date || null,
      tasks_completed: c?.tasks_completed || 0,
      skills_completed: c?.skills_completed || 0,
      updated_at: new Date().toISOString(),
    };

    if (action === 'diary_entry') {
      const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
      if (c?.last_diary_date === today) return;
      const newStreak = c?.last_diary_date === yesterday ? (c?.diary_streak || 0) + 1 : 1;
      updates.diary_streak = newStreak;
      updates.longest_diary_streak = Math.max(newStreak, c?.longest_diary_streak || 0);
      updates.last_diary_date = today;
    }

    if (action === 'task_complete') {
      updates.tasks_completed = (c?.tasks_completed || 0) + 1;
    }
    if (action === 'skill_complete') {
      updates.skills_completed = (c?.skills_completed || 0) + 1;
    }

    const { error } = await (supabase as any)
      .from('user_gamification')
      .upsert(updates, { onConflict: 'user_id' });

    if (error) {
      console.error('[Gamification] upsert error:', error);
      return;
    }

    // Log to points_history
    await (supabase as any)
      .from('points_history')
      .insert({
        user_id: userId,
        action,
        points,
        description: description || ACTION_LABELS[action],
      });

    // XP toast
    toast(`+${points} XP`, {
      description: ACTION_LABELS[action],
      duration: 2500,
    });

    // Streak milestone notification
    if (action === 'diary_entry' && updates.diary_streak > 1) {
      const streak = updates.diary_streak as number;
      setTimeout(() => {
        if (streak === 3) toast('🔥 3-day streak! Keep it up!', { duration: 3000 });
        else if (streak === 7) toast('🔥 7-day streak! One week strong!', { duration: 3000 });
        else if (streak === 30) toast('🔥 30-day streak! Incredible!', { duration: 3500 });
        else if (streak % 10 === 0) toast(`🔥 ${streak}-day streak! Amazing!`, { duration: 3000 });
        else toast(`🔥 ${streak}-day streak!`, { duration: 2000 });
      }, 800);
    }
  } catch (err) {
    console.error('[Gamification] error:', err);
  }
}
